<i18n>
{
  "en": {
    "productIndexingTitle": "Product Indexing",
    "addProductIndexing": "Add a product indexing",
    "editError": "Error editing product indexing",
    "createError": "Error creating product indexing",
    "productIndexingListError": "Error requesting list of product indexing",
    "noIndexing": "No Product Indexing available",
    "productListError": "Error requesting list of products",
    "deleteTitle": "Are you sure you want to delete this product indexing?",
    "deleteError": "There was an error deleting the boost",
    "delete": "Delete",
    "saveError": "There was an error saving the rule, try again later.",
    "saveDelay": "This process may take a while. Please hold on until it finishes."
  },
  "es": {
    "productIndexingTitle": "Indexación de productos",
    "addProductIndexing": "Añadir una indexación de productos",
    "editError": "Error editando indexación",
    "createError": "Error creando indexación",
    "productIndexingListError": "Error pidiendo los datos de indexación de productos",
    "noIndexing": "No hay indexaciones de productos disponibles",
    "productListError": "Error pidiendo los datos de los productos",
    "deleteTitle": "¿Estás seguro de que quieres borrar esta indexación de producto?",
    "deleteError": "Ha habido un error borrando el product boost",
    "delete": "Borrar",
    "saveError": "Hubo un error actualizando la regla de indexación, inténtelo de nuevo más tarde.",
    "saveDelay": "Este proceso puede tardar un rato. Por favor, espere a que termine."
  }
}
</i18n>
<template>
  <v-container>
    <div class="d-grid custom-grid">
      <div
        class="text-center font-weight-bold ma-1 text-md-h6 text-body-1 indexing-title grid-center-x grid-center-y">
        {{ $t('productIndexingTitle') }}
      </div>
      <v-tooltip top>
        <template v-slot:activator="{ on, attrs }">
          <v-btn icon class="mr-2 indexing-button grid-end-x grid-center-y" v-on="on" v-bind="attrs"
                 @click="onCreate">
            <v-icon color="grey" large>mdi-plus</v-icon>
          </v-btn>
        </template>
        <span>{{ $t('addProductIndexing') }}</span>
      </v-tooltip>
    </div>
    <div v-if="isEmpty"
         class="d-flex justify-space-around grey--text mt-5">
      <span>{{ $t('noIndexing') }}</span>
    </div>
    <dynameat-skeleton v-if="isLoading" skeleton-type="forecastDemand" :container="false"
                       class="mt-5"/>
    <v-list v-else class="mb-16">
      <product-indexing-card v-for="productIndexing in productIndexingList"
                             :key="productIndexing.referenceProductGuid"
                             :productIndexing="productIndexing"
                             @delete="onDelete"
                             @edit="onEdit"/>
    </v-list>
    <product-indexing-modal :main-product-list="masterProductList"
                            :associated-products="associatedProducts"
                            :edit-mode="editMode"
                            :product-indexing="selectedProductIndexing"
                            :product-indexing-guid="selectedProductIndexingGuid"
                            :visible="modalVisible"
                            @createConfirm="onCreateConfirm"
                            @editConfirm="onEditConfirm"
                            @cancel="onCancel"
    />
    <dynameat-confirmation-modal :visible="deleteModalVisible" :title="$t('deleteTitle')"
                                 :confirm-text="$t('delete')"
                                 @confirm="onConfirmDeleteModal" @cancel="onCloseDeleteModal"/>
  </v-container>
</template>
<script>
import Vue from 'vue';
import Component from 'vue-class-component';
import { Components, MapActions, MapGetters } from '@core/helpers/decorators';
import DynameatSkeleton from '@dynameat/components/DynameatSkeleton';
import { createErrorNotification, createWarningNotification } from '@ulibs/unnotificationsqueue';
import DynameatConfirmationModal from '@dynameat/components/DynameatConfirmationModal';
import { Watch } from 'vue-property-decorator';
import ProductIndexingModal from './components/ProductIndexingModal.vue';
import ProductIndexingCard from './components/ProductIndexingCard.vue';

@Component
@MapGetters(['isLoading', 'productIndexingList', 'masterProductList'])
@MapActions(['productIndexingListRequest', 'masterProductListRequest', 'setLoading',
  'removeProductIndexingRequest', 'editProductIndexingRequest', 'createProductIndexingRequest',
  'addNotification'])
@Components({
  ProductIndexingCard, DynameatSkeleton, ProductIndexingModal, DynameatConfirmationModal
})
export default class ProductIndexing extends Vue {
  editMode = false;

  selectedProductIndexing = null;

  selectedProductIndexingGuid = '';

  modalVisible = false;

  productIndexingToDelete = null;

  deleteModalVisible = false;

  get associatedProducts() {
    return new Set(this.productIndexingList
      .flatMap((indexing) => indexing.associatedProducts.map((associated) => associated.associatedProductGuid)));
  }

  get isEmpty() {
    return !this.isLoading && this.productIndexingList.length === 0;
  }

  async created() {
    await this.getProductIndexingList();
    await this.loadManagedProductListData();
  }

  async getProductIndexingList() {
    try {
      this.setLoading(1);
      await this.productIndexingListRequest(this.$route.params.pointOfSaleGuid);
    } catch {
      this.addNotification(createErrorNotification(this.$t('productIndexingListError')));
    } finally {
      this.setLoading(-1);
    }
  }

  async loadManagedProductListData() {
    try {
      await this.masterProductListRequest(this.$route.params.pointOfSaleGuid);
    } catch (e) {
      this.addNotification(createErrorNotification(this.$t('productListError')));
    }
  }

  onDelete(payload) {
    this.productIndexingToDelete = payload;
    this.deleteModalVisible = true;
  }

  onConfirmDeleteModal() {
    try {
      this.removeProductIndexingRequest(this.productIndexingToDelete);
    } catch (e) {
      this.addNotification(createErrorNotification(this.$t('deleteError')));
    } finally {
      this.deleteModalVisible = false;
    }
  }

  onEdit({ productIndexing, productIndexingGuid }) {
    this.editMode = true;
    this.selectedProductIndexing = productIndexing;
    this.selectedProductIndexingGuid = productIndexingGuid;
    this.modalVisible = true;
  }

  onCreate() {
    this.editMode = false;
    this.selectedProductIndexing = null;
    this.selectedProductIndexingGuid = null;
    this.modalVisible = true;
  }

  onCancel() {
    this.modalVisible = false;
  }

  onEditConfirm({ productIndexing, productIndexingGuid }) {
    this.modalVisible = false;
    try {
      this.editProductIndexingRequest({ productIndexing, productIndexingGuid });
      this.addNotification(createWarningNotification(this.$t('saveDelay')));
    } catch (e) {
      this.addNotification(createErrorNotification(this.$t('editError')));
    }
  }

  onCreateConfirm({ productIndexing }) {
    this.modalVisible = false;
    try {
      this.createProductIndexingRequest(productIndexing);
      this.addNotification(createWarningNotification(this.$t('saveDelay')));
    } catch (e) {
      this.addNotification(createErrorNotification(this.$t('createError')));
    }
  }

  onCloseDeleteModal() {
    this.productIndexingToDelete = null;
    this.deleteModalVisible = false;
  }

  @Watch('$route')
  async onRouteChange(to, from) {
    if (to.params.pointOfSaleGuid !== from.params.pointOfSaleGuid) {
      await this.getProductIndexingList();
      await this.loadManagedProductListData();
    }
  }
}
</script>

<style scoped>
.custom-grid {
  grid-template-columns: auto auto auto;
  grid-template-rows: auto;
  grid-template-areas:
    "nothing title button";
}

.indexing-title {
  grid-area: title;
}

.indexing-button {
  grid-area: button;
}

</style>
